<template>
  <div class="issues" :class="userRoleGroup" v-if="userRoleGroup">
    <wombat-table
      class="projects-table"
      :items="issues"
      :columns="tableColumns"
      :resize="false"
      :loadingItems="false"
      :columnsReorder="false"
      :itemHeight="58"
      :noResultsMessage="$t('noResultsFound')"
    >
      <component
        @deleteIssueClicked="deleteIssueClickedHandler"
        @editIssueClicked="editIssueClickedHandler"
        @showIssueDetails="showIssueDetailsHandler"
        slot="row-cell"
        slot-scope="rowCell"
        class="row-cell"
        :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
        :item="rowCell.item"
        :column="rowCell.column"
      ></component>
    </wombat-table>

    <v-btn
      v-if="userRoleGroup === userGroups.SP"
      class="new-issue-button"
      color="primary"
      @click="displayNewIssuePopup"
    >
      {{ $t('issueList.create.finding') }}
    </v-btn>

    <new-issue-popup
      @closed="onCloseNewIssuePopup"
      @saved="onSaveNewIssuePopup"
      :project-id="detailedProject.id"
      :visible-popup="showNewIssuePopup"
    >
    </new-issue-popup>
    <issue-details-popup
      @closed="onCloseIssueDetailsPopup"
      :project-id="detailedProject.id"
      :visible-popup="showIssueDetailsPopup"
    >
    </issue-details-popup>
    <edit-issue-popup
      @closed="onCloseEditIssuePopup"
      @saved="onSaveEditIssuePopup"
      :project-id="detailedProject.id"
      :issue-id="issueIdToEdit"
      :visible-popup="showEditIssuePopup"
    >
    </edit-issue-popup>

    <yes-no-popup
      :visible-popup="showDeleteIssueConfirmationPopup"
      title="issue.delete.popup.title"
      message="issue.delete.popup.message"
      yesCaption="confirm.ok"
      noCaption="confirm.cancel"
      @clicked="onDeleteIssueClickYesNoButton"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { views } from '@/router/constants';
import { userRoleGroups } from '@/constants/auth';
import { projectStatuses } from '@/constants/projects';
import {
  LOAD_ISSUES, LOAD_ISSUE_DETAILS, DELETE_ISSUE, CLEAR_ISSUE_DETAILS,
} from '@/store/issue/actionTypes';

import NewIssuePopup from '@/components/popup/NewIssuePopup.vue';
import IssueDetailsPopup from '@/components/popup/IssueDetailsPopup.vue';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import EditIssuePopup from '@/components/popup/EditIssuePopup.vue';
import { issueColumns, getEmptyColumn } from '@/services/tableColumns';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import IssueSecurityRiskTableCell from '@/components/tableCells/issueTableCells/IssueSecurityRiskTableCell.vue';
import IssueDescriptionTableCell from '@/components/tableCells/issueTableCells/IssueDescriptionTableCell.vue';
import IssueProbabilityTableCell from '@/components/tableCells/issueTableCells/IssueProbabilityTableCell.vue';
import IssueSeverityTableCell from '@/components/tableCells/issueTableCells/IssueSeverityTableCell.vue';
import IssueRiskScoreTableCell from '@/components/tableCells/issueTableCells/IssueRiskScoreTableCell.vue';
import IssueStatusTableCell from '@/components/tableCells/issueTableCells/IssueStatusTableCell.vue';

export default {
  name: 'IssuesList',
  components: {
    NewIssuePopup,
    IssueDetailsPopup,
    YesNoPopup,
    EditIssuePopup,
    WombatTable,
    WombatRowDefaultCell,
    IssueSecurityRiskTableCell,
    IssueDescriptionTableCell,
    IssueRiskScoreTableCell,
    IssueStatusTableCell,
  },
  data() {
    return {
      showNewIssuePopup: false,
      showEditIssuePopup: false,
      showIssueDetailsPopup: false,
      showDeleteIssueConfirmationPopup: false,
      issueIdToDelete: null,
      issueIdToEdit: null,
    };
  },
  computed: {
    ...mapGetters(['issues', 'userRoleGroup', 'detailedProject']),
    userGroups() {
      return userRoleGroups;
    },
    isUserSP() {
      return this.userRoleGroup === userRoleGroups.SP;
    },
    tableColumns() {
      const columns = [
        issueColumns.securityRisk,
        issueColumns.description,
        issueColumns.probability,
        issueColumns.severity,
        issueColumns.riskScore,
        issueColumns.status,
      ].map(column => ({
        ...column,
        title: column.title && this.$t(column.title),
      }));

      return [getEmptyColumn(32), ...columns, getEmptyColumn(85)];
    },
    tableRowCellComponentsHash() {
      return {
        securityRisk: IssueSecurityRiskTableCell,
        description: IssueDescriptionTableCell,
        probability: IssueProbabilityTableCell,
        severity: IssueSeverityTableCell,
        riskScore: IssueRiskScoreTableCell,
        status: IssueStatusTableCell,
        default: WombatRowDefaultCell,
      };
    },
  },
  watch: {
    userRoleGroup() {
      if (this.isUserSP && this.detailedProject.status !== projectStatuses.IN_PROGRESS) {
        this.$router.replace({ name: views.PROJECT_GENERAL_INFORMATION });
      }
    },
  },
  async mounted() {
    await this.$store.dispatch(LOAD_ISSUES, { projectId: this.detailedProject.id });
  },
  methods: {
    displayNewIssuePopup() {
      this.showNewIssuePopup = true;
    },
    onCloseNewIssuePopup() {
      this.showNewIssuePopup = false;
    },
    onSaveNewIssuePopup() {
      this.showNewIssuePopup = false;
    },
    async onCloseEditIssuePopup() {
      this.showEditIssuePopup = false;
      await this.$store.dispatch(CLEAR_ISSUE_DETAILS);
    },
    async onSaveEditIssuePopup() {
      this.showEditIssuePopup = false;
      await this.$store.dispatch(CLEAR_ISSUE_DETAILS);
    },
    onCloseIssueDetailsPopup() {
      this.showIssueDetailsPopup = false;
    },
    async showIssueDetailsHandler(issueId) {
      await this.$store.dispatch(LOAD_ISSUE_DETAILS, { issueId });
      this.showIssueDetailsPopup = true;
    },
    deleteIssueClickedHandler(issueId) {
      this.showDeleteIssueConfirmationPopup = true;
      this.issueIdToDelete = issueId;
    },
    async editIssueClickedHandler(issueId) {
      this.showEditIssuePopup = true;
      await this.$store.dispatch(LOAD_ISSUE_DETAILS, { issueId });
      this.issueIdToEdit = issueId;
    },
    async onDeleteIssueClickYesNoButton(result) {
      this.showDeleteIssueConfirmationPopup = false;
      if (result) {
        const options = { issueId: this.issueIdToDelete, projectId: this.detailedProject.id };
        await this.$store.dispatch(DELETE_ISSUE, options);
      }
      this.issueIdToDelete = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.issues {
  width: 100%;
  position: relative;

  ::v-deep.wombat-table {
    height: 100% !important;
  }

  ::v-deep.wombat-header {
    padding-top: rem(10);

    .column-status .wombat-header-default-cell {
      float: right;
    }
  }

  ::v-deep.wombat-row {
    .status.column.issue-status {
      top: auto;
    }

    .actions {
      display: none;
      width: max-content;

      .action {
        display: inline;
        padding: rem(6) rem(2) rem(7);
      }
    }
    .mark-as-fixed {
      display: none;
    }

    .mark-issue-as-fixed-button {
      top: rem(-2);
      right: rem(21);
    }

    &:hover {
      .issue-status.class-column-wrapper {
        display: none;
      }
      .actions {
        display: inline-block;
      }
      .mark-as-fixed {
        display: inline-block;
      }
    }

    .issue-risk-score {
      padding-top: rem(18);
    }

    .change-issue-status-button {
      top: rem(-2);
      right: rem(20);
    }
  }

  .issues-header {
    display: block;
    padding-left: rem(15);
    padding-top: rem(16);

    .header {
      display: inline-block;

      color: var(--text-tertiary);
      font-size: rem(10);
      letter-spacing: rem(0.8);
      line-height: rem(10);
      text-transform: uppercase;
    }
  }

  .probability {
    width: rem(120);
    text-transform: capitalize;
  }

  .severity {
    width: rem(108);
    text-transform: capitalize;
  }

  .risk-score {
    width: rem(115);
  }

  .status {
    width: rem(68);
    text-transform: capitalize;
    display: inline-block;
    text-align: right;
  }

  .column {
    padding-top: rem(15);
    padding-right: rem(5);
  }

  .new-issue-button {
    position: absolute;
    color: $white;
    font-size: rem(12);
    font-weight: 500;
    letter-spacing: rem(1.07);
    line-height: rem(16);
    text-align: center;
    top: rem(-40);
    right: rem(86);
  }
}
</style>
